import { action, observable } from 'mobx';
import { container, singleton } from 'tsyringe';

import { errorHandling } from '../../utils/error-handling';
import { ErrorPriority } from '../error';
import { LocalizationStore } from '../localization';

import {
  addDiacriticToKeyboard,
  changeKeyCase,
  fetchLanguages,
  initKeyboard,
  removeDiacriticFromKeyboard,
  setupKeyboard,
} from './keyboard-service';
import { KeyboardMap, KeyboardLanguagesMap, KeyboardType, Keyboard } from './types';

const SPECIAL_LANGUAGE = 'Special';
const DIACRITIC_ON_FOCUS_TIMEOUT = 800;

export interface KeyboardStoreInitArgs {
  type?: KeyboardType;
  value?: string;
  preload?: boolean;
}
/**
 * @category Store
 */
@singleton()
export class KeyboardStore {
  static moduleName = 'KeyboardStore';

  @observable currentLanguage?: string;
  @observable keyboard?: Keyboard;
  @observable activeOriginDiacritic?: string;
  @observable value = '';
  @observable isSpecial = false;
  @observable isShift = false;
  @observable isReady = false;

  public type: KeyboardType = KeyboardType.login;
  public fullWidth?: number;
  public fullHeight?: number;
  public firstFocusIndexX?: number;
  public firstFocusIndexY?: number;

  private keyboardLanguagesMap?: KeyboardLanguagesMap;
  private keyboardMap?: KeyboardMap;
  private keyboardsStaticPath = '';
  private languageLoaded? = '';

  private languagesArray: string[] = [];
  private currentIndex?: number;

  private activeDiacriticTimer?: NodeJS.Timeout;
  private localizationStore: LocalizationStore;

  constructor() {
    this.localizationStore = container.resolve<LocalizationStore>('localizationStore');
  }

  setUp(keyboardsStaticPath = '') {
    this.keyboardsStaticPath = keyboardsStaticPath;
  }

  private async loadKeyboadMap() {
    if (this.languageLoaded === this.localizationStore.displayLanguage) {
      return;
    }
    const languages: { [key: string]: string } = {};
    const languagesArray: string[] = [];

    this.localizationStore.languagesList.map((language) => {
      languages[language.name] = `${this.keyboardsStaticPath}${language.name}.json`;
      languagesArray.push(language.name);
    });

    this.languagesArray = languagesArray;
    languages[SPECIAL_LANGUAGE] = `${this.keyboardsStaticPath}${SPECIAL_LANGUAGE}.json`;

    this.keyboardLanguagesMap = await fetchLanguages(Object.entries(languages));
    this.languageLoaded = this.localizationStore.displayLanguage;
  }

  /**
   * Keyboard initialization
   *
   * @param type - type of keyboard
   * @param value - initial value
   * @param preload - preload languages for keyboards
   */
  @action
  @errorHandling({ type: ErrorPriority.high })
  async init({ type, value = '', preload = false }: KeyboardStoreInitArgs) {
    if (!type && !preload) {
      throw new Error('KeyboardStore.init - type or preload are required');
    }

    await this.loadKeyboadMap();
    if (preload) {
      return;
    }
    this.type = type as KeyboardType;
    this.value = value;

    this.currentLanguage = this.defaultLanguage;
    this.currentIndex = this.languagesArray.findIndex((item: string) => item === this.currentLanguage);

    if (this.currentLanguage && this.keyboardLanguagesMap) {
      this.keyboardMap = this.keyboardLanguagesMap[this.currentLanguage][this.type];
    }

    if (this.keyboardMap) {
      const { fullWidth, fullHeight, firstFocusIndexY, firstFocusIndexX } = initKeyboard(this.keyboardMap);
      this.firstFocusIndexX = firstFocusIndexX;
      this.firstFocusIndexY = firstFocusIndexY;
      this.fullWidth = fullWidth;
      this.fullHeight = fullHeight;
      this.keyboard = setupKeyboard(this.keyboardMap);
    }
    this.isReady = true;
  }

  @action
  changeLanguage() {
    this.isSpecial = false;
    this.isShift = false;
    const nextIndex = this.calculateNextIndex(this.currentIndex);
    const nextLanguage = this.languagesArray[nextIndex];

    if (this.keyboardLanguagesMap && nextLanguage) {
      this.keyboardMap = this.keyboardLanguagesMap[nextLanguage][this.type];
      this.currentIndex = nextIndex;
      this.currentLanguage = nextLanguage;
    }

    if (this.keyboardMap) {
      this.keyboard = setupKeyboard(this.keyboardMap);
    }
  }

  @action
  changeSpecial() {
    this.isSpecial = !this.isSpecial;
    this.isShift = false;

    if (this.isSpecial && this.keyboardLanguagesMap) {
      this.keyboardMap = this.keyboardLanguagesMap[SPECIAL_LANGUAGE][this.type];
    } else if (this.currentLanguage && this.keyboardLanguagesMap) {
      this.keyboardMap = this.keyboardLanguagesMap[this.currentLanguage][this.type];
    }
    if (this.keyboardMap) {
      this.keyboard = setupKeyboard(this.keyboardMap);
    }
  }

  @action
  setFocusKey(y: number, x: number) {
    if (!this.keyboard) {
      return;
    }

    if (this.activeDiacriticTimer) {
      clearTimeout(this.activeDiacriticTimer);
    }

    const key = this.isShift ? this.keyboard[y][x] : this.keyboard[y][x];
    if (this.activeOriginDiacritic !== key.value && this.activeOriginDiacritic !== key.diacriticForValue) {
      removeDiacriticFromKeyboard(this.keyboard, this.isShift);
      this.activeOriginDiacritic = undefined;
      this.keyboard = [...this.keyboard];
    }
    const diacriticKeys = key.diacritic[this.isShift ? 'upper' : 'lower'];
    if (!this.activeOriginDiacritic && diacriticKeys.length) {
      this.activeDiacriticTimer = setTimeout(() => {
        if (!this.keyboard) {
          return;
        }
        this.activeOriginDiacritic = key.value;
        addDiacriticToKeyboard(this.keyboard, key.value, diacriticKeys, y, x);
        this.keyboard = [...this.keyboard];
      }, DIACRITIC_ON_FOCUS_TIMEOUT);
    }
  }

  @action
  hideDiacritic() {
    if (this.activeDiacriticTimer) {
      clearTimeout(this.activeDiacriticTimer);
    }

    // @ts-expect-error TODO Fix
    removeDiacriticFromKeyboard(this.keyboard, this.isShift);
    this.activeOriginDiacritic = undefined;
    // @ts-expect-error TODO Fix
    this.keyboard = [...this.keyboard];
  }

  @action
  onToggleShift() {
    this.isShift = !this.isShift;
  }

  @action
  onEnter(keyValue: string): string {
    let newValue = this.value;
    switch (keyValue) {
      case 'image_shift': {
        this.isShift = !this.isShift;
        if (this.keyboard) {
          changeKeyCase(this.keyboard, this.isShift);
        }
        break;
      }
      case 'image_space': {
        newValue += ' ';
        break;
      }
      case 'image_delete': {
        newValue = newValue.slice(0, -1);
        break;
      }
      default: {
        newValue += keyValue;
        if (this.isShift && this.keyboard) {
          this.isShift = false;
          changeKeyCase(this.keyboard, this.isShift);
        }
      }
    }
    this.value = newValue;
    return newValue;
  }

  @action
  resetFocus() {
    if (this.activeDiacriticTimer) {
      clearTimeout(this.activeDiacriticTimer);
    }

    if (this.keyboard) {
      removeDiacriticFromKeyboard(this.keyboard, this.isShift);
    }
    this.activeOriginDiacritic = undefined;
  }

  @action
  reset() {
    this.isReady = false;
    this.activeOriginDiacritic = undefined;
    this.isShift = false;
    this.isSpecial = false;
    this.value = '';
    this.currentLanguage = undefined;
  }

  private calculateNextIndex(currentIndex?: number) {
    if (currentIndex !== undefined && this.languagesArray.length) {
      if (currentIndex + 1 < this.languagesArray.length) {
        return currentIndex + 1;
      }
    }
    // start from the beginning
    return 0;
  }

  private get defaultLanguage() {
    if (!this.localizationStore.defaultLanguage) {
      return;
    }
    return this.localizationStore.languageNameByCode(this.localizationStore.defaultLanguage);
  }
}
